/**
 * widgets/WidgetShell.tsx — JEDINÝ renderer widgetu. Vlastní veškerý povinný
 * chrome (hlavička karty) a všech pět stavů. Widget dodá jen tělo přes
 * WidgetRenderProps — na hlavičku/titulek/akce nemá přístup, takže je nemůže
 * vynechat ani přemalovat (determinismus z konstrukce, ne z wiki).
 *
 * Interpretace zpětné vazby (dialog s Filipem 2026-07-11):
 *  - transport/práva/dostupnost → ZDE, genericky (inactive/error/placeholder/loading)
 *  - sémantika výsledku toolu   → def.interpret() (jen doménová)
 *  - agregace stavů             → onFeedback → spona/panel/status registr
 *
 * Data: deklarativní `def.mcp` tahá SHELL (přes useWidgetData) a předá je
 * komponentě jako `data`. Kompozitní widget (bez `mcp`) si data řeší sám, ale
 * pořád dostane chrome + availability + error boundary.
 */
import { Component, type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../lib/i18n';
import { AlertTriangle, Loader2, Lock, PowerOff, RefreshCw } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { StatusDot } from '../components/ui/StatusDot';
import { Badge } from '../components/ui/Badge';
import { WidgetCallError } from './scope';
import { useNodeModules, useWidgetData } from './hooks';
import { useNodeDisplayLabel } from './NodePicker';
import type { EffectiveScope, Viewport, WidgetDef, WidgetFormat, WidgetState } from './types';

/** Phone UI = úzký viewport NEBO vynucený Metro layout (data-layout=phone). */
function usePreferPhoneBody(viewport: Viewport): boolean {
  const [layoutPhone, setLayoutPhone] = useState(() =>
    typeof document !== 'undefined' && document.documentElement.dataset.layout === 'phone'
  );
  useEffect(() => {
    const read = () => setLayoutPhone(document.documentElement.dataset.layout === 'phone');
    read();
    window.addEventListener('acm-ui-layout', read);
    const obs = typeof MutationObserver !== 'undefined'
      ? new MutationObserver(read)
      : null;
    obs?.observe(document.documentElement, { attributes: true, attributeFilter: ['data-layout'] });
    return () => {
      window.removeEventListener('acm-ui-layout', read);
      obs?.disconnect();
    };
  }, []);
  return viewport === 'mobile' || layoutPhone;
}

// Mapování WidgetState → StatusDot v hlavičce (per-karta feedback žije v chrome).
const STATE_DOT: Record<WidgetState, string> = {
  ready: 'running',
  loading: 'pending',
  error: 'error',
  inactive: 'stopped',
  placeholder: 'stopped',
};

export interface WidgetShellProps {
  def: WidgetDef;
  scope: EffectiveScope;
  format: WidgetFormat;
  viewport: Viewport;
  /** Default nod záložky — když scope.node ≠ tento, karta míří jinam (override → chip). */
  panelNode?: string | null;
  editing?: boolean;
  /** Slot pro edit akce (drag handle, formát, node picker, odebrat) — dá je panel. */
  editActions?: ReactNode;
  /** Reportuje výsledný stav karty nahoru (spona/panel → agregace → registr). */
  onFeedback?: (state: WidgetState, message?: string) => void;
}

export function WidgetShell(
  { def, scope, format, viewport, panelNode, editing, editActions, onFeedback }: WidgetShellProps,
) {
  const { t } = useTranslation();
  const localLabel = t('common.localNode', 'Lokál');
  const nodeLabel = useNodeDisplayLabel(scope.node, localLabel);
  const needsModule = def.module;
  const { modules, isLoading: modLoading } = useNodeModules(scope.node);
  const moduleMissing = !!needsModule && modules !== null && !modules.has(needsModule);

  // Deklarativní data tahá shell; kompozitní widget (bez mcp) hook nespustí.
  const dataQuery = useWidgetData(
    def.mcp?.tool,
    def.mcp?.args ?? {},
    scope,
    { enabled: !moduleMissing && !!def.mcp, refetchInterval: def.refetchInterval },
  );

  // ── Odvození stavu (transport/práva/dostupnost genericky, sémantika přes interpret)
  let state: WidgetState = 'ready';
  let message: string | undefined;
  let data: unknown = undefined;

  if (moduleMissing) {
    state = 'inactive';
    message = t('widgets.moduleMissingOnNode', {
      module: needsModule,
      node: nodeLabel,
    });
  } else if (def.mcp) {
    if (dataQuery.isLoading || (modLoading && needsModule)) {
      state = 'loading';
    } else if (dataQuery.isError) {
      const err = dataQuery.error as Error;
      if (err instanceof WidgetCallError && err.kind === 'forbidden') {
        state = 'placeholder';
        message = t('widgets.noPermission', 'Nemáte oprávnění');
      } else {
        state = 'error';
        message = err?.message || t('widgets.callFailed', 'Call failed');
      }
    } else {
      data = dataQuery.data;
      // Sémantická interpretace — JEN doménová, řeší widget.
      if (def.interpret) {
        const v = def.interpret(data);
        state = v.state;
        message = v.message;
      }
    }
  }

  useEffect(() => {
    onFeedback?.(state, message);
  }, [state, message, onFeedback]);

  const preferPhone = usePreferPhoneBody(viewport);
  // phone body když existuje a běžíme v telefonním UI; jinak desktop component
  const Comp = preferPhone && def.phone ? def.phone : def.component;
  const isOverride = (scope.node ?? null) !== (panelNode ?? null);
  // Reset klíč pro error boundary — když se změní cíl nebo se data přenačtou,
  // boundary se remountuje a případná stará chyba zmizí (jinak zůstane „zaseknutá").
  const boundaryKey = `${scope.node ?? 'local'}:${scope.identity ?? 'self'}:${dataQuery.dataUpdatedAt}`;

  return (
    <Card className='h-full flex flex-col'>
      {/* POVINNÁ hlavička karty — chrome, kreslí shell, ne widget */}
      <div className='flex items-center gap-2 px-3 py-2 border-b border-gray-800/50 bg-gray-950/20 shrink-0'>
        {def.icon ? <def.icon size={14} className='text-plex shrink-0 opacity-80' /> : null}
        <span className='text-sm font-semibold text-gray-100 truncate'>
          {t(`widgets.registry.${def.id}`, def.title)}
        </span>
        <StatusDot status={STATE_DOT[state]} />
        <div className='ml-auto flex items-center gap-1.5'>
          <Badge variant={isOverride ? 'purple' : 'gray'}>{nodeLabel}</Badge>
          {scope.identity && <Badge variant='blue'>@{scope.identity.slice(0, 8)}</Badge>}
          {editing ? editActions : null}
        </div>
      </div>

      {/* Tělo — dle stavu buď widget, nebo standardní stavová výplň.
          Odsazení p-3/p-4: bez něj custom panely (npm, docker…) leží nalepené
          na okrajích karty a vypadají „neodsazené". Stavové fill zůstávají full. */}
      <div className='flex-1 min-h-0 overflow-auto'>
        {state === 'inactive' && (
          <StateFill icon={PowerOff} title={message ?? t('widgets.unavailable', 'Nedostupné')} muted />
        )}
        {state === 'placeholder' && (
          <StateFill icon={Lock} title={message ?? t('widgets.noPermission', 'Nemáte oprávnění')} muted />
        )}
        {state === 'error' && (
          <WidgetError message={message ?? t('widgets.error', 'Error')} onRetry={() => dataQuery.refetch()} />
        )}
        {state === 'loading' && <StateFill icon={Loader2} title={t('common.loading', 'Načítám…')} spin />}
        {state === 'ready' && (
          <div className='p-3 sm:p-4'>
            <WidgetBoundary key={boundaryKey} title={t(`widgets.registry.${def.id}`, def.title)}>
              <Comp format={format} viewport={viewport} scope={scope} data={data} />
            </WidgetBoundary>
          </div>
        )}
      </div>
    </Card>
  );
}

function StateFill(
  { icon: Icon, title, muted, spin }: {
    icon: React.ComponentType<{ size?: number | string; className?: string }>;
    title: string;
    muted?: boolean;
    spin?: boolean;
  },
) {
  return (
    <div
      className={`h-full w-full flex flex-col items-center justify-center gap-2 p-4 text-center ${
        muted ? 'opacity-50 grayscale' : ''
      }`}
    >
      <Icon size={22} className={`text-gray-500 ${spin ? 'animate-spin' : ''}`} />
      <span className='text-xs text-gray-500'>{title}</span>
    </div>
  );
}

export function WidgetError({ message, onRetry }: { message: string; onRetry?: () => void }) {
  const { t } = useTranslation();
  return (
    <div className='h-full w-full flex flex-col items-center justify-center gap-3 p-4 text-center'>
      <div className='acm-alert acm-alert-destructive max-w-sm w-full px-4 py-3 text-left'>
        <div className='flex gap-2.5 items-start'>
          <AlertTriangle size={16} className='text-red-400 shrink-0 mt-0.5' />
          <span className='text-xs text-red-300 break-words leading-relaxed'>{message}</span>
        </div>
      </div>
      {onRetry && (
        <button
          type='button'
          onClick={onRetry}
          className='flex items-center gap-1 text-xs text-gray-400 hover:text-gray-100 rounded-lg px-2 py-1 hover:bg-gray-800/50 transition-colors'
        >
          <RefreshCw size={12} /> {t('widgets.retry', 'Try again')}
        </button>
      )}
    </div>
  );
}

// Error boundary kolem těla widgetu — kompozitní widget který hodí runtime chybu
// nesmí shodit celý panel; zobrazí se standardní error stav (viz #310 lekce).
class WidgetBoundary extends Component<{ title: string; children: ReactNode }, { err: Error | null }> {
  state = { err: null as Error | null };
  static getDerivedStateFromError(err: Error) {
    return { err };
  }
  render() {
    if (this.state.err) {
      return (
        <WidgetError
          message={this.state.err.message || i18n.t('widgets.crashed', {
            title: this.props.title,
            defaultValue: 'Widget "{{title}}" crashed',
          })}
        />
      );
    }
    return this.props.children;
  }
}
